import axios from 'axios'
import { toast } from 'react-toastify'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import classes from './FamiliesPage.module.scss'
import MembersSearchInput from './components/MembersSearchInput'
import FilterButton from './components/FilterButton'
import { deserialize } from '../../utility/serialization'
import { requestFamilies } from '../../actions/familyActions'
import FamiliesTable from './components/FamiliesTable'
import { useOutletContext } from 'react-router-dom'
import TablePlaceholder from './components/TablePlaceholder'
import { debounce } from '../../utility/debounceHelper'
import { PER_PAGE_DEFAULT } from '../../misc/Constants'

const FILTER_OPTIONS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
}

const FamiliesPage = () => {
  const { t } = useTranslation()
  const { loadedFamiliesData } = useOutletContext()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [families, setFamilies] = useState(loadedFamiliesData?.families)
  const [pagination, setPagination] = useState(loadedFamiliesData?.meta.pagination)
  const [searchText, setSearchText] = useState('')
  const [filterOption, setFilterOption] = useState(FILTER_OPTIONS.ALL)
  const [, setIsLoading] = useState(false)

  const fetchFamilies = useCallback(
    ({ page, search, active, append }) =>
      requestFamilies(communityId, page, PER_PAGE_DEFAULT, search, active, abortController.current?.signal)
        .then(response => {
          const data = deserialize(response.data)
          setFamilies(prev => {
            if (append) { return [...prev, ...data.families] }
            return data.families
          })
          setPagination(data.meta.pagination)
        }),
    [communityId]
  )

  const updateFamilies = useCallback(
    (params) => {
      setIsLoading(true)
      fetchFamilies({
        page: 1,
        search: searchText,
        active: filterOption === FILTER_OPTIONS.ACTIVE,
        append: false,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => setIsLoading(false))
    },
    [fetchFamilies, filterOption, searchText]
  )

  const fetchMoreFamilies = () => {
    updateFamilies({ page: pagination?.page + 1, append: true })
  }

  const debouncedSearch = useMemo(
    () => debounce((search) => updateFamilies({ search })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleFilter = (option) => {
    setFilterOption(option)
    updateFamilies({ active: option === FILTER_OPTIONS.ACTIVE })
  }


  return (
    <main className={classes.wrapper}>
      <header className='py-3 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <MembersSearchInput placeholder={t('members.search.families')} value={searchText} onChange={handleSearch} />
        </div>
        <div className={classes.filterOptions}>
          <FilterButton
            text={t('members.filters.all')}
            onClick={() => handleFilter(FILTER_OPTIONS.ALL)}
            isActive={filterOption === FILTER_OPTIONS.ALL}
          />
          <FilterButton
            text={t('members.filters.active')}
            onClick={() => handleFilter(FILTER_OPTIONS.ACTIVE)}
            isActive={filterOption === FILTER_OPTIONS.ACTIVE}
          />
        </div>
      </header>
      {families?.length === 0 ? (
        <TablePlaceholder message={t('members.familiesPage.emptyState')} />
      ) : (
        <FamiliesTable families={families} fetchData={fetchMoreFamilies} pagination={pagination} />
      )}
    </main>
  )
}

export default FamiliesPage
